// Cloud sync for the payments ledger (Cloudflare D1 backend). A "workspace" is a
// token; the ledger is stored server-side keyed by it. Opening a ?ledger=<ws>
// link (or enabling sync) joins this browser to that workspace so it syncs across
// devices and can be shared with a team.
import type { PaymentsData } from "../payments/types";

const WS_KEY = "cg:ledger-ws";
const PASS_KEY = "cg:ledger-pass";

export function getPass(): string {
  try { return localStorage.getItem(PASS_KEY) || ""; } catch { return ""; }
}
export function setPass(p: string) {
  try { if (p) localStorage.setItem(PASS_KEY, p); else localStorage.removeItem(PASS_KEY); } catch { /* ignore */ }
}
function passHeaders(): Record<string, string> {
  const p = getPass();
  return p ? { "x-ledger-pass": p } : {};
}
export class ProtectedError extends Error {
  protected = true;
  constructor() { super("passcode required"); }
}
export class NeedLoginError extends Error {
  needLogin = true;
  constructor() { super("sign in required"); }
}
export class ForbiddenError extends Error {
  forbidden = true;
  constructor() { super("no access to this ledger"); }
}
export class ReadonlyError extends Error {
  readonly = true;
  constructor() { super("read-only access"); }
}
async function throwAccess(res: Response): Promise<never> {
  const b = (await res.json().catch(() => ({}))) as any;
  if (b.needLogin) throw new NeedLoginError();
  if (b.readonly) throw new ReadonlyError();
  if (b.forbidden) throw new ForbiddenError();
  throw new ProtectedError();
}

function newToken(): string {
  try {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 20);
  } catch {
    return "w" + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }
}

/** Current workspace: from a ?ledger= link (persisted) or previously stored. */
export function getWorkspace(): string | null {
  try {
    const url = new URLSearchParams(window.location.search).get("ledger");
    if (url && /^[A-Za-z0-9_-]{6,64}$/.test(url)) {
      localStorage.setItem(WS_KEY, url);
      return url;
    }
    return localStorage.getItem(WS_KEY);
  } catch {
    return null;
  }
}
export function setWorkspace(ws: string): void {
  try { localStorage.setItem(WS_KEY, ws); } catch { /* ignore */ }
}
export function ensureWorkspace(): string {
  let ws = getWorkspace();
  if (!ws) {
    ws = newToken();
    try { localStorage.setItem(WS_KEY, ws); } catch { /* ignore */ }
  }
  return ws;
}
export function workspaceLink(ws: string): string {
  return `${window.location.origin}/?ledger=${ws}`;
}

export interface RemoteLedger {
  exists: boolean;
  version?: number;
  data?: PaymentsData;
  protected?: boolean;
  owned?: boolean;
  isOwner?: boolean;
  role?: string; // owner | editor | viewer
}
export async function getRemote(ws: string): Promise<RemoteLedger> {
  const res = await fetch(`/api/ledger/${ws}`, { headers: passHeaders() });
  if (res.status === 401 || res.status === 403) return throwAccess(res);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export type PutResult = { version: number } | { conflict: true; version: number; data: PaymentsData };
export async function putRemote(ws: string, data: PaymentsData, baseVersion?: number, opts?: { setPasscode?: string }): Promise<PutResult> {
  const payload: any = { data, baseVersion };
  if (opts && "setPasscode" in opts) payload.setPasscode = opts.setPasscode;
  const res = await fetch(`/api/ledger/${ws}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...passHeaders() },
    body: JSON.stringify(payload),
  });
  if (res.status === 401 || res.status === 403) return throwAccess(res);
  const body = (await res.json()) as any;
  if (res.status === 409) return { conflict: true, version: body.version, data: body.data };
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return { version: body.version };
}
